import useUtilStore from "@/data/util";
import { StoreEnum } from "@/enums";
import {
  IBoundary,
  ICategories,
  IDashboardValuesPHC,
  IMaybe,
  ISelect,
  ISubCategories,
} from "@/types";
import { StateCreator } from "zustand";
import { devtools } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
import useLayerStore from "./Layer";

export interface IndicatorState {
  selectedIndicator: ISubCategories[];
  indicatorType: IMaybe<string[]>;
  indicatorList: IMaybe<ISelect[]>;
  categoryList: IMaybe<ICategories[]>;
  dashboardData: IMaybe<IDashboardValuesPHC>;
  indicatorData: IMaybe<IBoundary>;
}

export interface IndicatorActions {
  setSelectedIndicator: (indicator: ISubCategories[]) => void;
  setIndicatorType: (indicatorType: IMaybe<string[]>) => void;
  setIndicatorList: (indicatorList: IMaybe<ISelect[]>) => void;
  setCategoryList: (categoryList: IMaybe<ICategories[]>) => void;
  setIndicatorData: (indicatorData: IMaybe<IBoundary>) => void;
  setDashboardData: (dashboardData: IMaybe<IDashboardValuesPHC>) => void;
  clearBoundaryByIndicator: () => void;
}

export const storeCreator: StateCreator<IndicatorState & IndicatorActions> = (
  set
) => ({
  selectedIndicator: [],
  indicatorType: null,
  indicatorList: null,
  categoryList: null,
  dashboardData: null,
  indicatorData: null,
  setSelectedIndicator: (selectedIndicator) => set({ selectedIndicator }),
  setDashboardData: (dashboardData) =>
    set((state) =>
      shallow(state.dashboardData, dashboardData) ? state : { dashboardData }
    ),
  setIndicatorData: (indicatorData) => set({ indicatorData }),
  clearBoundaryByIndicator: async () => {
    const { setDashboardData, setIndicatorData } = useIndicatorStore.getState();
    const { setIsLoading } = useUtilStore.getState();
    const { setMaxValue, resetLayer } = useLayerStore.getState();

    setDashboardData({
      total_count: 0,
      total_number_of_sba: 0,
      total_sba_gaps: 0,
      total_facility_functionality_1: 0,
      total_facility_functionality_2: 0,
      total_facility_functionality_partial: 0,
      total_number_of_medical_officers: 0,
      total_number_of_nurses: 0,
      total_number_of_chos: 0,
      total_number_of_jchews: 0,
      total_number_of_schews: 0,
      total_number_of_pharmacy_techs: 0,
      total_number_of_lab_techs: 0,
      total_bhcpf_impact_funds: 0,
      functional_backup_power: 0,
      staff_accommodation_availability: 0,
      phc_count: [],
      geo_data: [],
    });
    setIndicatorData(null);
    setMaxValue(null);
    resetLayer();
    setIsLoading(false);
  },
  setIndicatorType: async (indicatorType) => set({ indicatorType }),
  setIndicatorList: (indicatorList) => set({ indicatorList }),
  setCategoryList: (categoryList) => set({ categoryList }),
});

const useIndicatorStore = createWithEqualityFn<
  IndicatorState & IndicatorActions
>(
  devtools(storeCreator, { name: StoreEnum.INDICATOR }) as StateCreator<
    IndicatorState & IndicatorActions
  >,
  shallow
);

export default useIndicatorStore;
